import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import './AdminDashboard.css';

// AML boolean flags with Slovenian labels (order matches the spec form).
const RISK_FLAGS = [
  { key: 'personally_known', label: 'Stranka osebno poznana' },
  { key: 'existing_customer', label: 'Obstoječa stranka' },
  { key: 'instructions_via_third_party', label: 'Navodila prek tretje osebe' },
  { key: 'outside_slo', label: 'Izven Slovenije' },
  { key: 'outside_eu', label: 'Izven EU' },
  { key: 'suspicious_referral', label: 'Sumljivo priporočilo' },
  { key: 'aml_suspicion', label: 'Sum pranja denarja' },
  { key: 'higher_risk', label: 'Višje tveganje' },
  { key: 'high_turnover', label: 'Visok promet' },
  { key: 'reported', label: 'Prijavljeno' },
  { key: 'crime_link', label: 'Povezava s kaznivim dejanjem' },
  { key: 'provided_id_data', label: 'Predložil identifikacijske podatke' },
  { key: 'cooperative', label: 'Sodelujoč' },
  { key: 'in_person', label: 'Osebna prisotnost' },
  { key: 'logical_transaction', label: 'Logična transakcija' },
  { key: 'consistent_data', label: 'Skladni podatki' },
  { key: 'rejected', label: 'Zavrnjeno' }
];

function defaultRisk() {
  return {
    personally_known: false, existing_customer: true, instructions_via_third_party: false,
    outside_slo: false, outside_eu: false, suspicious_referral: false, aml_suspicion: false,
    higher_risk: false, high_turnover: false, reported: false, crime_link: false,
    provided_id_data: true, cooperative: true, in_person: true, logical_transaction: true,
    consistent_data: true, rejected: false, overall_risk: 'nizko', risk_notes: ''
  };
}

const yn = (v) => (v ? 'Da' : 'Ne');

function AdminDashboard() {
  const { adminFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [risk, setRisk] = useState(null);
  const [savingRisk, setSavingRisk] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, [filter, search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await adminFetch('/api/admin/stats');
      const statsData = await statsRes.json();
      if (statsRes.ok) {
        setStats(statsData);
      }

      // Fetch customers
      let url = '/api/admin/customers?limit=50';
      if (filter !== 'all') url += `&status=${filter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const customersRes = await adminFetch(url);
      const customersData = await customersRes.json();
      if (customersRes.ok) {
        setCustomers(customersData.customers || []);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateCustomerStatus = async (customerId, status) => {
    try {
      const res = await adminFetch(`/api/admin/customers/${customerId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        fetchData();
        setSelectedCustomer(null);
        setRisk(null);
      }
    } catch (err) {
      console.error('Failed to update customer:', err);
    }
  };

  const viewCustomer = async (customerId) => {
    try {
      const res = await adminFetch(`/api/admin/customers/${customerId}`);
      const data = await res.json();
      if (res.ok) {
        setSelectedCustomer(data);
        setRisk(data.risk_assessment || defaultRisk());
      }
    } catch (err) {
      console.error('Failed to fetch customer:', err);
    }
  };

  const closeModal = () => {
    setSelectedCustomer(null);
    setRisk(null);
  };

  const toggleRisk = (flag) => {
    setRisk(prev => ({ ...prev, [flag]: !prev[flag] }));
  };

  const saveRisk = async () => {
    if (!selectedCustomer) return;
    setSavingRisk(true);
    try {
      const res = await adminFetch(`/api/admin/customers/${selectedCustomer.customer.id}/risk-assessment`, {
        method: 'PATCH',
        body: JSON.stringify(risk)
      });
      const data = await res.json();
      if (res.ok) {
        setRisk(data.risk_assessment);
      }
    } catch (err) {
      console.error('Failed to save risk assessment:', err);
    } finally {
      setSavingRisk(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Nalaganje...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <h1>Admin Nadzorna plošča</h1>
      
      {/* Stats Cards */}
      {stats && (
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="stat-number">{stats.totalCustomers || 0}</div>
            <div className="stat-label">Vsi uporabniki</div>
          </div>
          <div className="admin-stat-card verified">
            <div className="stat-number">{stats.verifiedCustomers || 0}</div>
            <div className="stat-label">Verificirani</div>
          </div>
          <div className="admin-stat-card pending">
            <div className="stat-number">{stats.pendingCustomers || 0}</div>
            <div className="stat-label">V čakanju</div>
          </div>
          <div className="admin-stat-card suspended">
            <div className="stat-number">{stats.suspendedCustomers || 0}</div>
            <div className="stat-label">Suspendirani</div>
          </div>
          <div className="admin-stat-card">
            <div className="stat-number">{stats.recentRegistrations || 0}</div>
            <div className="stat-label">Novi (7 dni)</div>
          </div>
          <div className="admin-stat-card gold">
            <div className="stat-number">€{stats.totalPurchases?.toFixed(0) || 0}</div>
            <div className="stat-label">Skupni nakupi</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="admin-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Išči po emailu, imenu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Vsi statusi</option>
            <option value="incomplete">Nedokončani</option>
            <option value="verified">Verificirani</option>
            <option value="pending">V čakanju</option>
            <option value="suspended">Suspendirani</option>
            <option value="rejected">Zavrnjeni</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={fetchData}>
          Osveži
        </button>
      </div>

      {/* Customers Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>E-mail</th>
              <th>Ime</th>
              <th>Status</th>
              <th>Registracija</th>
              <th>Zadnja prijava</th>
              <th>Nakupi</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer) => (
              <tr key={customer.id}>
                <td>{customer.email}</td>
                <td>{customer.is_legal_entity ? customer.company_name : `${customer.first_name || ''} ${customer.last_name || ''}`}</td>
                <td>
                  <span className={`status-badge status-${customer.status}`}>
                    {customer.status}
                  </span>
                </td>
                <td>{new Date(customer.created_at).toLocaleDateString('sl-SI')}</td>
                <td>
                  {customer.last_login 
                    ? new Date(customer.last_login).toLocaleDateString('sl-SI')
                    : '-'}
                </td>
                <td>€{customer.total_purchases?.toFixed(2) || '0.00'}</td>
                <td>
                  <button 
                    className="btn-action"
                    onClick={() => viewCustomer(customer.id)}
                  >
                    Podrobnosti
                  </button>
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan="7" className="empty-state">
                  Ni uporabnikov za prikaz
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (() => {
        const c = selectedCustomer.customer;
        const owners = selectedCustomer.beneficial_owners || [];
        const documents = selectedCustomer.documents || [];
        const highRiskTrigger = c.sanctions_connection || c.pep_status ||
          (c.buy_volume_per_year && parseFloat(c.buy_volume_per_year) > 15000);
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('sl-SI') : '-';

        return (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Podrobnosti stranke</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="customer-detail-grid">
                <div className="detail-section">
                  <h3>{c.is_legal_entity ? 'Pravna oseba' : 'Fizična oseba'}</h3>
                  <div className="detail-row"><span>E-mail:</span><span>{c.email}</span></div>
                  {c.is_legal_entity ? (
                    <>
                      <div className="detail-row"><span>Naziv:</span><span>{c.company_name || '-'}</span></div>
                      <div className="detail-row"><span>Pravna oblika:</span><span>{c.legal_form || '-'}</span></div>
                      <div className="detail-row"><span>Davčna podjetja:</span><span>{c.company_tax_number || '-'}</span></div>
                      <div className="detail-row"><span>Matična:</span><span>{c.company_registration_number || '-'}</span></div>
                      <div className="detail-row"><span>Država reg.:</span><span>{c.company_register_country || '-'}</span></div>
                    </>
                  ) : (
                    <div className="detail-row"><span>Ime:</span><span>{c.first_name} {c.last_name}</span></div>
                  )}
                  <div className="detail-row"><span>Telefon:</span><span>{c.phone || '-'}</span></div>
                  <div className="detail-row">
                    <span>Status:</span>
                    <span className={`status-badge status-${c.status}`}>{c.status}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Naslov in rojstvo</h3>
                  <div className="detail-row"><span>Naslov:</span><span>{c.address_street || '-'}</span></div>
                  <div className="detail-row"><span>Kraj:</span><span>{c.address_postal_code} {c.address_city}</span></div>
                  <div className="detail-row"><span>Država:</span><span>{c.address_country || '-'}</span></div>
                  {c.has_temp_address && (
                    <div className="detail-row"><span>Začasni:</span><span>{c.temp_address_street}, {c.temp_address_postal_code} {c.temp_address_city}</span></div>
                  )}
                  <div className="detail-row"><span>Datum rojstva:</span><span>{fmtDate(c.birth_date)}</span></div>
                  <div className="detail-row"><span>Kraj rojstva:</span><span>{c.birthplace || '-'}</span></div>
                  <div className="detail-row"><span>Davčna:</span><span>{c.tax_number || '-'}</span></div>
                  <div className="detail-row"><span>EMŠO:</span><span>{c.emso || '-'}</span></div>
                </div>

                <div className="detail-section">
                  <h3>Dokument</h3>
                  <div className="detail-row"><span>Tip:</span><span>{c.id_doc_type || '-'}</span></div>
                  <div className="detail-row"><span>Številka:</span><span>{c.id_doc_number || '-'}</span></div>
                  <div className="detail-row"><span>Izdajatelj:</span><span>{c.id_doc_issuer || '-'}</span></div>
                  <div className="detail-row"><span>Izdaja:</span><span>{fmtDate(c.id_doc_issue_date)}</span></div>
                  <div className="detail-row"><span>Poteče:</span><span>{c.id_doc_permanent ? 'Trajno' : fmtDate(c.id_doc_expiry_date)}</span></div>
                  <div className="detail-row"><span>Shufti:</span><span>{c.shufti_verification_status || '-'}</span></div>
                </div>

                <div className="detail-section">
                  <h3>Državljanstvo in PEP</h3>
                  <div className="detail-row"><span>Državljanstvo:</span><span>{c.citizenship || '-'}</span></div>
                  <div className="detail-row"><span>Dvojno:</span><span>{yn(c.dual_citizenship)} {c.dual_citizenship_country ? `(${c.dual_citizenship_country})` : ''}</span></div>
                  <div className="detail-row"><span>Sankcije:</span><span>{yn(c.sanctions_connection)}</span></div>
                  <div className="detail-row"><span>PEP:</span><span>{yn(c.pep_status)}</span></div>
                  {c.pep_details && <div className="detail-row"><span>PEP opis:</span><span>{c.pep_details}</span></div>}
                </div>

                <div className="detail-section">
                  <h3>Transakcija</h3>
                  <div className="detail-row"><span>Letni obseg:</span><span>{c.buy_volume_per_year ? `€${c.buy_volume_per_year}` : '-'}</span></div>
                  <div className="detail-row"><span>Namen:</span><span>{c.purpose || '-'}{c.purpose_other ? ` (${c.purpose_other})` : ''}</span></div>
                  <div className="detail-row"><span>Plačilo:</span><span>{c.payment_method || '-'}</span></div>
                  <div className="detail-row"><span>Viri sredstev:</span><span>{c.source_of_funds?.join(', ') || '-'}</span></div>
                  <div className="detail-row"><span>Priporočilo:</span><span>{c.recommendation_from || '-'}</span></div>
                </div>

                {c.is_legal_entity && (
                  <div className="detail-section">
                    <h3>Dejanski lastniki</h3>
                    {owners.length === 0 && <div className="detail-row"><span>-</span></div>}
                    {owners.map((o) => (
                      <div key={o.id} className="detail-row">
                        <span>{o.full_name}</span>
                        <span>{o.ownership_share ? `${o.ownership_share}%` : ''} {o.is_pep ? '· PEP' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="detail-section">
                  <h3>Dokumenti</h3>
                  {documents.length === 0 && <div className="detail-row"><span>-</span></div>}
                  {documents.map((d) => (
                    <div key={d.id} className="detail-row">
                      <span>{d.doc_type}:</span>
                      <span>
                        {d.download_url
                          ? <a href={d.download_url} target="_blank" rel="noopener noreferrer">{d.original_name || 'prenos'}</a>
                          : (d.original_name || '-')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk assessment / AML panel */}
              {risk && (
                <div className="risk-panel">
                  <h3>Ocena tveganja</h3>
                  {highRiskTrigger && (
                    <div className="risk-trigger-note">
                      ⚠️ Samodejno zaznano višje tveganje (sankcije / PEP / obseg &gt; 15.000 €)
                    </div>
                  )}

                  <div className="risk-flags-grid">
                    {RISK_FLAGS.map((f) => (
                      <label key={f.key} className="risk-flag">
                        <input type="checkbox" checked={!!risk[f.key]} onChange={() => toggleRisk(f.key)} />
                        {f.label}
                      </label>
                    ))}
                  </div>

                  <div className="risk-overall">
                    <label>Skupna ocena tveganja</label>
                    <div className="risk-rating-options">
                      {['nizko', 'srednje', 'visoko'].map((level) => (
                        <label key={level} className={`risk-rating risk-rating-${level} ${risk.overall_risk === level ? 'selected' : ''}`}>
                          <input type="radio" name="overall_risk" checked={risk.overall_risk === level}
                            onChange={() => setRisk(prev => ({ ...prev, overall_risk: level }))} />
                          {level === 'nizko' ? 'NIZKO (SPS)' : level === 'srednje' ? 'SREDNJE' : 'VISOKO (POPS)'}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-field">
                    <label>Opombe</label>
                    <textarea rows="2" value={risk.risk_notes || ''}
                      onChange={(e) => setRisk(prev => ({ ...prev, risk_notes: e.target.value }))} />
                  </div>

                  <button className="btn btn-primary" onClick={saveRisk} disabled={savingRisk}>
                    {savingRisk ? 'Shranjevanje...' : 'Shrani oceno tveganja'}
                  </button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="status-actions">
                <button className="btn btn-success"
                  onClick={() => updateCustomerStatus(c.id, 'verified')}
                  disabled={c.status === 'verified'}>✓ Verificiraj</button>
                <button className="btn btn-warning"
                  onClick={() => updateCustomerStatus(c.id, 'pending')}
                  disabled={c.status === 'pending'}>⏳ V čakanje</button>
                <button className="btn btn-danger"
                  onClick={() => updateCustomerStatus(c.id, 'rejected')}
                  disabled={c.status === 'rejected'}>✕ Zavrni</button>
                <button className="btn btn-danger"
                  onClick={() => updateCustomerStatus(c.id, 'suspended')}
                  disabled={c.status === 'suspended'}>⚠️ Suspendiraj</button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default AdminDashboard;

